import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /** @type {boolean} */
  #cartUpdateInFlight = false;
  /** @type {Set<HTMLButtonElement | HTMLInputElement>} */
  #temporarilyDisabledControls = new Set();
  /** @type {Promise<unknown>} */
  #pendingOptimisticRemoval = Promise.resolve();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.#cartUpdateInFlight) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (this.#cartUpdateInFlight) return;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      this.#pendingOptimisticRemoval = startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    const removalPromises = rowsToRemove.map((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        remove();
        return Promise.resolve();
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      return onAnimationEnd(row, remove);
    });
    this.#pendingOptimisticRemoval = Promise.allSettled(removalPromises);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (this.#cartUpdateInFlight) return;

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const lineId = lineItemRow?.dataset.key ?? '';
    const quantityInput = lineItemRow?.querySelector('input');
    const previousQuantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.defaultValue) : null;
    const sectionSnapshot = document.getElementById(buildSectionSelector(this.sectionId))?.outerHTML ?? null;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      let parsedResponseText;

      try {
        parsedResponseText = JSON.parse(responseText);
      } catch {
        parsedResponseText = null;
      }

      const errorMessage = this.#getCartResponseError(response, parsedResponseText);

      if (errorMessage || !parsedResponseText) {
        const reconciledCart = await this.#reconcileCartMutation(lineId, previousQuantity, quantity);

        if (reconciledCart.applied || reconciledCart.changed) {
          const partiallyApplied = !reconciledCart.applied && reconciledCart.changed;
          await this.#applyCartMutationResult(reconciledCart.cart, {}, deferredUpdatePromise, partiallyApplied);
          if (partiallyApplied) {
            const partialErrorMessage = errorMessage || 'Failed to update cart';
            this.#showCartError(lineId, partialErrorMessage);
            this.#dispatchCartError(partialErrorMessage, 'INVALID');
          }
          return;
        }

        await this.#restoreFailedCartMutation(sectionSnapshot, lineId, errorMessage || 'Failed to update cart');
        const error = new Error(errorMessage || 'Failed to update cart');
        deferredUpdatePromise.reject(error);
        this.#dispatchCartError(error.message, response.ok ? 'SERVICE_UNAVAILABLE' : 'INVALID');
        return;
      }

      await this.#applyCartMutationResult(
        parsedResponseText,
        parsedResponseText.sections ?? {},
        deferredUpdatePromise,
        false
      );
    } catch (error) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update cart';
      const reconciledCart = await this.#reconcileCartMutation(lineId, previousQuantity, quantity);

      if (reconciledCart.applied || reconciledCart.changed) {
        const partiallyApplied = !reconciledCart.applied && reconciledCart.changed;
        await this.#applyCartMutationResult(reconciledCart.cart, {}, deferredUpdatePromise, partiallyApplied);
        if (partiallyApplied) {
          this.#showCartError(lineId, errorMessage);
          this.#dispatchCartError(errorMessage, 'INVALID');
        }
        return;
      }

      await this.#restoreFailedCartMutation(
        sectionSnapshot,
        lineId,
        errorMessage
      );
      deferredUpdatePromise.reject(error);
      this.#dispatchCartError(errorMessage, 'SERVICE_UNAVAILABLE');
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Returns a customer-facing error from Shopify's supported Cart API error shapes.
   * @param {Response} response - The Cart API response.
   * @param {any} parsedResponse - The parsed response body.
   * @returns {string | null}
   */
  #getCartResponseError(response, parsedResponse) {
    if (parsedResponse?.errors) {
      return typeof parsedResponse.errors === 'string'
        ? parsedResponse.errors
        : JSON.stringify(parsedResponse.errors);
    }

    if (response.ok) return null;

    return parsedResponse?.description || parsedResponse?.message || response.statusText || 'Failed to update cart';
  }

  /**
   * Checks Shopify cart truth after an ambiguous/error response. A bundled-section
   * error can return HTTP 400 even though the cart mutation already succeeded.
   * @param {string} lineId - The cart line key.
   * @param {number | null} previousQuantity - Quantity before the request.
   * @param {number} requestedQuantity - Requested quantity.
   * @returns {Promise<{cart: any, applied: boolean, changed: boolean}>}
   */
  async #reconcileCartMutation(lineId, previousQuantity, requestedQuantity) {
    try {
      const cart = await this.fetchCartData();
      const currentQuantity = cart.items?.find((item) => item.key === lineId)?.quantity ?? 0;
      return {
        cart,
        applied: currentQuantity === requestedQuantity,
        changed: currentQuantity !== previousQuantity,
      };
    } catch {
      return { cart: null, applied: false, changed: false };
    }
  }

  /**
   * Resolves the canonical cart event, then refreshes the current section without
   * ever repeating the mutation when bundled section HTML is absent or malformed.
   * @param {any} cart - Shopify Ajax cart payload.
   * @param {Record<string, string | null>} sections - Bundled section HTML.
   * @param {{resolve: (value: any) => void}} deferredUpdatePromise - Standard event deferred promise.
   * @param {boolean} didError - Whether Shopify returned an error while changing cart truth.
   */
  async #applyCartMutationResult(cart, sections, deferredUpdatePromise, didError) {
    await Promise.allSettled([this.#pendingOptimisticRemoval]);
    resetShimmer(this);
    this.#updateQuantitySelectors(cart);

    deferredUpdatePromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count ?? 0,
        source: 'cart-items-component',
        didError,
      },
    });

    const currentSectionHtml = sections[this.sectionId];
    try {
      if (typeof currentSectionHtml === 'string' && currentSectionHtml.length > 0) {
        try {
          await morphSection(this.sectionId, currentSectionHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        } catch {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-items] Unable to refresh cart section:', error);
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Restores the last coherent section after a confirmed mutation failure.
   * @param {string | null} sectionSnapshot - Section HTML captured before the request.
   * @param {string} lineId - The cart line key.
   * @param {string} errorMessage - Customer-facing error text.
   */
  async #restoreFailedCartMutation(sectionSnapshot, lineId, errorMessage) {
    await Promise.allSettled([this.#pendingOptimisticRemoval]);
    resetShimmer(this);

    if (sectionSnapshot) {
      try {
        await morphSection(this.sectionId, sectionSnapshot, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Unable to restore cart section:', error);
      }
    }

    this.#showCartError(lineId, errorMessage);
  }

  /**
   * Resets the affected control and exposes its existing line-level live error.
   * @param {string} lineId - The cart line key.
   * @param {string} errorMessage - Customer-facing error text.
   */
  #showCartError(lineId, errorMessage) {
    const currentComponent =
      document
        .getElementById(buildSectionSelector(this.sectionId))
        ?.querySelector(`cart-items-component[data-section-id="${CSS.escape(this.sectionId)}"]`) ?? this;
    const cartItemRow = currentComponent.querySelector(`[data-key="${CSS.escape(lineId)}"]`);
    const quantityInput = cartItemRow?.querySelector('input');
    const cartItemError = cartItemRow?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = cartItemRow?.querySelector('.cart-items__error');

    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;
    if (cartItemError instanceof HTMLElement) cartItemError.textContent = errorMessage;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');
  }

  /**
   * Dispatches the existing standardized cart error event.
   * @param {string} message - Error message.
   * @param {string} code - Standard error code.
   */
  #dispatchCartError(message, code) {
    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code,
      })
    );
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.#cartUpdateInFlight = true;
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');

    for (const control of this.querySelectorAll(
      'cart-quantity-selector-component button, cart-quantity-selector-component input, button.cart-items__remove'
    )) {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement) || control.disabled) continue;

      control.disabled = true;
      this.#temporarilyDisabledControls.add(control);
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.#cartUpdateInFlight = false;
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');

    for (const control of this.#temporarilyDisabledControls) {
      control.disabled = false;
    }
    this.#temporarilyDisabledControls.clear();
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
